import React, { useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Grid, Switch, Avatar, Tooltip, Button, Typography, Dropdown, Badge } from 'antd';
import {
  DashboardOutlined, AppstoreOutlined, PictureOutlined,
  HistoryOutlined, PoweroffOutlined, UserOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, MoonOutlined, SunOutlined,
  NotificationOutlined,
  CompassOutlined,
} from '@ant-design/icons';
import { GiNotebook, GiCardExchange } from 'react-icons/gi';
import { FaArrowsDownToPeople } from 'react-icons/fa6';
import { MdOutlineHub } from 'react-icons/md';
import { DarkModeContext } from '../../context/DarkModeContext';
import { useAuth } from '../../context/AuthContext';
import { useOrderSocketEvents } from '../../context/OrderSocketContext';
import {
  getHubPlanId,
  resolveUserSubscriptionAccess,
} from '../../utils/subscriptionState';
import {
  buildProfileSubscriptionSummary,
} from '../../utils/subscriptionDisplay';
import {
  getSubscriptionRouteRequirement,
} from '../../config/subscriptionCatalogConfig';
import {
  resolveCatalogEntitlement,
} from '../../services/subscriptionCatalog';
import { SUBSCRIPTION_GATE_LABELS } from '../../labels/subscriptionGateLabels';
import ProfileDropdown from './ProfileDropdown';
import { FOOTER, ADMIN_SHELL } from '../../config/zinihubConfig';
import { GUIDED_JOURNEY_LABELS } from '../../labels/guidedJourneyLabels';
import { useGuidedJourney } from '../GuidedJourney';
import styles from '../../styles/AdminAppShell.module.css';

const { Header, Content, Footer, Sider } = Layout;
const { useBreakpoint } = Grid;
const { Text } = Typography;

const ICON_MAP = {
  '/dashboard':    <DashboardOutlined />,
  '/hub':          <AppstoreOutlined />,
  '/gallery':      <PictureOutlined />,
  '/notebook':     <GiNotebook />,
  '/staff':        <FaArrowsDownToPeople />,
  '/subscription': <GiCardExchange />,
  '/hubSettings':  <MdOutlineHub />,
  '/log':          <HistoryOutlined />,
};

function groupOrderAlerts(orderAlerts = []) {
  const grouped = new Map();
  orderAlerts.forEach((alert) => {
    const orderNumber = alert.orderNumber || 'N/A';
    if (!grouped.has(orderNumber)) grouped.set(orderNumber, []);
    grouped.get(orderNumber).push(alert);
  });
  return Array.from(grouped.entries()).map(([orderNumber, alerts]) => ({ orderNumber, alerts }));
}

function resolveSelectedKey(pathname) {
  if (pathname.startsWith('/hubSettings')) return '/hubSettings';
  if (pathname.startsWith('/hub') && pathname !== '/hubSettings') return '/hub';
  if (pathname.startsWith('/gallery')) return '/gallery';
  return pathname;
}

export default function AdminAppShell({ children, pageTitle }) {
  const { darkMode, setDarkMode } = useContext(DarkModeContext);
  const { logout, decodeToken, token, subscriptionCatalog } = useAuth();
  const { pendingOrderAlertCount, orderAlerts, latestOrderAlert, acknowledgeOrderEvents } = useOrderSocketEvents();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = screens.md === false;
  const { restartJourney } = useGuidedJourney();

  const [collapsed, setCollapsed] = useState(false);
  const [mobileSiderOpen, setMobileSiderOpen] = useState(false);
  const menuInlineCollapsed = !isMobile && collapsed;
  const showSidebarLabels = isMobile || !collapsed;

  const toggleDesktopSidebar = useCallback(() => {
    setCollapsed((previous) => !previous);
  }, []);

  const toggleMobileSidebar = useCallback(() => {
    setMobileSiderOpen((previous) => !previous);
  }, []);

  const closeMobileSidebar = useCallback(() => {
    setMobileSiderOpen(false);
  }, []);

  useEffect(() => {
    if (!isMobile || !mobileSiderOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMobileSidebar();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [closeMobileSidebar, isMobile, mobileSiderOpen]);

  useEffect(() => {
    closeMobileSidebar();
  }, [closeMobileSidebar, location.pathname]);

  useEffect(() => {
    if (!isMobile) closeMobileSidebar();
  }, [closeMobileSidebar, isMobile]);
  const [orderAlertOpen, setOrderAlertOpen] = useState(false);
  const [expandedOrderAlerts, setExpandedOrderAlerts] = useState(new Set());
  const decoded = decodeToken(token);
  const subscriptionAccess = useMemo(
    () => resolveUserSubscriptionAccess(decoded),
    [decoded],
  );
  const profileSubscriptionSummary = useMemo(
    () => buildProfileSubscriptionSummary(subscriptionCatalog, subscriptionAccess.hubProfile),
    [subscriptionAccess.hubProfile, subscriptionCatalog],
  );
  const navItems = useMemo(
    () => ADMIN_SHELL.NAV.map(({ key, label, comingSoon }) => {
      const requirement = getSubscriptionRouteRequirement(key);
      const entitlement = requirement && subscriptionCatalog
        ? resolveCatalogEntitlement(
            subscriptionCatalog,
            getHubPlanId(subscriptionAccess.hubProfile),
            requirement,
            subscriptionAccess.role,
          )
        : null;
      const subscriptionDisabled = Boolean(
        requirement && (!subscriptionAccess.active || !entitlement?.allowed),
      );
      const disabled = Boolean(comingSoon || subscriptionDisabled);
      const tooltip = comingSoon
        ? "Coming soon"
        : subscriptionDisabled
          ? SUBSCRIPTION_GATE_LABELS.navigationUnavailable
          : null;
      return {
        key,
        label: tooltip
          ? <Tooltip title={tooltip} placement="right" mouseEnterDelay={0.2}>
              <span style={{ display: 'block', width: '100%' }}>{label}</span>
            </Tooltip>
          : label,
        icon: ICON_MAP[key] || null,
        disabled,
        'aria-disabled': disabled ? 'true' : undefined,
      };
    }),
    [subscriptionAccess, subscriptionCatalog],
  );
  const userName = decoded?.['cognito:username'] || decoded?.email || ADMIN_SHELL.LABELS.BREADCRUMB_USER_FALLBACK;
  const activeKey = resolveSelectedKey(location.pathname);
  const navMatch = ADMIN_SHELL.NAV.find((n) => n.key === activeKey);
  const title = pageTitle || navMatch?.label || ADMIN_SHELL.LABELS.BREADCRUMB_PAGE_FALLBACK;
  const groupedOrderAlerts = groupOrderAlerts(orderAlerts);

  const handleMenuClick = ({ key }) => {
    if (isMobile) closeMobileSidebar();
    navigate(key);
  };

  const openDashboardFromAlert = () => {
    acknowledgeOrderEvents();
    setOrderAlertOpen(false);
    setExpandedOrderAlerts(new Set());
    navigate('/dashboard');
  };

  const toggleOrderAlertGroup = (orderNumber) => {
    setExpandedOrderAlerts((current) => {
      const next = new Set(current);
      if (next.has(orderNumber)) next.delete(orderNumber);
      else next.add(orderNumber);
      return next;
    });
  };

  const sidebarContent = (
    <div className={styles.sidebarInner}>
      <div className={`${styles.sidebarLogo} ${!showSidebarLabels ? styles.sidebarLogoCollapsed : ''}`}>
        <div className={styles.logoBadge}>{ADMIN_SHELL.BRAND.LOGO_LETTER}</div>
        {showSidebarLabels ? (
          <span className={styles.logoText}>{ADMIN_SHELL.BRAND.NAME}</span>
        ) : null}
        {!isMobile ? (
          <Tooltip
            title={collapsed ? ADMIN_SHELL.LABELS.EXPAND_SIDEBAR : ADMIN_SHELL.LABELS.COLLAPSE_SIDEBAR}
            placement="right"
          >
            <button
              type="button"
              className={styles.sidebarLogoCollapse}
              aria-label={collapsed ? ADMIN_SHELL.LABELS.EXPAND_SIDEBAR : ADMIN_SHELL.LABELS.COLLAPSE_SIDEBAR}
              onClick={toggleDesktopSidebar}
            >
              {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </button>
          </Tooltip>
        ) : null}
        {isMobile && mobileSiderOpen ? (
          <Button
            type="text"
            className={styles.mobileSidebarClose}
            icon={<MenuFoldOutlined />}
            aria-label={ADMIN_SHELL.LABELS.CLOSE_NAVIGATION}
            onClick={closeMobileSidebar}
          />
        ) : null}
      </div>

      <div className={styles.sidebarNav}>
        <Menu
          mode="inline"
          selectedKeys={[activeKey]}
          items={navItems}
          onClick={handleMenuClick}
          inlineCollapsed={menuInlineCollapsed}
          className={styles.sidebarMenu}
        />
      </div>

      <div
        className={`${styles.sidebarLogout} ${!showSidebarLabels ? styles.sidebarLogoutCollapsed : ''}`}
        onClick={restartJourney}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') restartJourney(); }}
      >
        <CompassOutlined />
        {showSidebarLabels ? <span>{GUIDED_JOURNEY_LABELS.launcher}</span> : null}
      </div>
      <div
        className={`${styles.sidebarLogout} ${!showSidebarLabels ? styles.sidebarLogoutCollapsed : ''}`}
        onClick={logout}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') logout(); }}
      >
        <PoweroffOutlined />
        {showSidebarLabels ? <span>{ADMIN_SHELL.LABELS.LOGOUT}</span> : null}
      </div>
    </div>
  );

  return (
    <Layout className={styles.layout}>
      {isMobile && mobileSiderOpen ? (
        <button
          type="button"
          className={styles.mobileSidebarBackdrop}
          aria-label={ADMIN_SHELL.LABELS.CLOSE_NAVIGATION}
          onClick={closeMobileSidebar}
        />
      ) : null}

      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          width={208}
          collapsedWidth={72}
          className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : ''}`}
        >
          {sidebarContent}
        </Sider>
      )}

      {isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={!mobileSiderOpen}
          collapsedWidth={0}
          width={240}
          className={`${styles.mobileSidebar} ${mobileSiderOpen ? styles.mobileSidebarOpen : styles.mobileSidebarClosed}`}
        >
          {sidebarContent}
        </Sider>
      )}

      <Layout className={styles.rightLayout} id="admin-content-scroll">
        {pendingOrderAlertCount > 0 && latestOrderAlert && (
          <div className={styles.floatingOrderAlert}>
            <button
              type="button"
              className={styles.floatingOrderAlertButton}
              onClick={() => setOrderAlertOpen((open) => !open)}
              aria-label="Open order alerts"
            >
              <span className={styles.floatingOrderAlertIcon}>
                <NotificationOutlined />
                <span className={styles.floatingOrderAlertPing} />
              </span>
              <span className={styles.floatingOrderAlertText}>{latestOrderAlert.label}</span>
              <span className={styles.floatingOrderAlertCount}>{pendingOrderAlertCount > 9 ? '9+' : pendingOrderAlertCount}</span>
            </button>

            {orderAlertOpen && (
              <div className={styles.floatingOrderAlertPanel}>
                <div className={styles.floatingOrderAlertTitle}>Order alerts</div>
                <div className={styles.floatingOrderAlertList}>
                  {groupedOrderAlerts.map(({ orderNumber, alerts }) => (
                    <div key={orderNumber} className={styles.floatingOrderAlertGroup}>
                      <button
                        type="button"
                        className={styles.floatingOrderAlertGroupHeader}
                        onClick={() => toggleOrderAlertGroup(orderNumber)}
                        aria-expanded={expandedOrderAlerts.has(orderNumber)}
                      >
                        <span>Order #{orderNumber}</span>
                        <span>{expandedOrderAlerts.has(orderNumber) ? '−' : '+'} {alerts.length}</span>
                      </button>
                      {expandedOrderAlerts.has(orderNumber) && (
                        <div className={styles.floatingOrderAlertGroupBody}>
                          {alerts.map((alert) => (
                            <div key={alert.id} className={styles.floatingOrderAlertDetail}>
                              <strong>{alert.label}</strong>
                              <span>{alert.detail}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className={styles.floatingOrderAlertActions}>
                  <button type="button" onClick={openDashboardFromAlert}>View Dashboard</button>
                  <button type="button" onClick={() => { acknowledgeOrderEvents(); setOrderAlertOpen(false); setExpandedOrderAlerts(new Set()); }}>Dismiss</button>
                </div>
              </div>
            )}
          </div>
        )}

        <Header className={styles.topbar}>
          <div className={styles.topbarLeft}>
            {isMobile ? (
              <Button
                type="text"
                icon={mobileSiderOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                aria-label={mobileSiderOpen ? ADMIN_SHELL.LABELS.CLOSE_NAVIGATION : ADMIN_SHELL.LABELS.OPEN_NAVIGATION}
                aria-expanded={mobileSiderOpen}
                onClick={toggleMobileSidebar}
              />
            ) : null}
            <span className={styles.breadcrumbBox} />
            <span className={styles.breadcrumbUser}>{userName}</span>
            <span className={styles.breadcrumbSep}>›</span>
            <span className={styles.breadcrumbPage}>{title}</span>
          </div>

          <div className={styles.topbarRight}>
            <Switch
              checkedChildren={<MoonOutlined />}
              unCheckedChildren={<SunOutlined />}
              checked={darkMode}
              onChange={setDarkMode}
              size="small"
            />
            <Dropdown
              dropdownRender={() => (
                <ProfileDropdown
                  decoded={decoded}
                  subscriptionCatalog={subscriptionCatalog}
                  hubProfile={subscriptionAccess.hubProfile}
                  onLogout={logout}
                  onManageSubscription={() => navigate('/subscription')}
                />
              )}
              trigger={['click']}
              placement="bottomRight"
            >
              <Badge
                count={profileSubscriptionSummary.avatarBadgeCount}
                size="small"
                offset={[-2, 2]}
                className={styles.avatarBadge}
              >
                <Avatar
                  size={24}
                  icon={<UserOutlined />}
                  className={styles.avatar}
                  style={{ cursor: 'pointer' }}
                />
              </Badge>
            </Dropdown>
          </div>
        </Header>

        <Content className={styles.content}>
          {children}
        </Content>

        <Footer className={styles.footer}>
          <Text className={styles.footerText}>
            {FOOTER.ADMIN}{' '}
            <Text className={styles.footerLink}>{FOOTER.BRAND_LINK}</Text>
          </Text>
        </Footer>
      </Layout>
    </Layout>
  );
}
